import moment from "moment";
import {
  PulseAnalysisErrorDetails,
  PulseAnalysisErrorResults,
  PulseBankAccount,
  PulseDocSummary,
  PulsePipelineStatus,
  PulseRangeErrorType,
  PulseRangeUploadInput,
  PulseUploadedForRange,
} from "./types";

export const NOT_SUBMITTED_STATUS = "NOT_SUBMITTED";
export const REQUIRED_PASSWORD_STATUS = "REQUIRED_PASSWORD";
export const SUBMITTING_STATUS = "SUBMITTING";
export const SUBMITTED_STATUS = "SUBMITTED";
export const OCR_PENDING_STATUS = "PENDING";
export const OCR_COMPLETED_STATUS = "COMPLETED";
export const ANALYSIS_FAILURE_STATUS = "FAILURE";
export const ANALYSIS_SUCCESS_STATUS = "VERIFICATION_SUCCESS";
export const STATEMENT_PROCESSING_FAILED_ERROR =
  "Statement processing failed";
export const MISSING_TRANSACTION_RANGES_ERROR = "Missing Transaction Ranges";
export const BALANCE_RECON_RANGES_ERROR = "Balance Recon Ranges";
export const MAX_FILE_SIZE = 20 * 1024 * 1024;
export const MAX_FILE_SIZE_LABEL = "20 MB";
export const PASSWORD_VERIFIED_DURATION_MS = 3_000;

export const CONSENTABLE_RANGE_KEYS = [
  "missing_transaction_ranges",
  "eod_balance_missing_ranges",
] as const;

const toRanges = (value: unknown): string[][] =>
  Array.isArray(value)
    ? value.filter(
        (range): range is string[] => Array.isArray(range) && range.length >= 2
      )
    : [];

export const getConsentableRanges = (
  errorDetails?: PulseAnalysisErrorDetails | null
): { key: PulseRangeErrorType; ranges: string[][] } => {
  for (const key of CONSENTABLE_RANGE_KEYS) {
    const ranges = toRanges(errorDetails?.errorResults?.[key]);
    if (ranges.length) return { key, ranges };
  }
  return { key: CONSENTABLE_RANGE_KEYS[0], ranges: [] };
};

export const getBalanceReconRanges = (
  errorResults?: PulseAnalysisErrorResults | null
): string[][] => toRanges(errorResults?.balance_recon_ranges);

// A range error shows only while its label is still in `errorDetails.errors`,
// so removing the label clears that error.
export const getVisibleRangeErrors = (
  errorDetails: PulseAnalysisErrorDetails | null | undefined
) => {
  const errors = errorDetails?.errors || [];
  const { key, ranges } = getConsentableRanges(errorDetails);
  return {
    consentableErrorType: key,
    transactionRanges: errors.includes(MISSING_TRANSACTION_RANGES_ERROR)
      ? ranges
      : [],
    balanceRanges: errors.includes(BALANCE_RECON_RANGES_ERROR)
      ? getBalanceReconRanges(errorDetails?.errorResults)
      : [],
  };
};

export const DIALOG_MODAL_STYLES = {
  maxWidth: "28.125rem",
  width: "calc(100vw - 2rem)",
};

export const ACTIVE_PIPELINE_STATUSES: PulsePipelineStatus[] = [
  "identifying",
  "processing",
];

export const isActivePipelineStatus = (status?: PulsePipelineStatus) =>
  !!status && ACTIVE_PIPELINE_STATUSES.includes(status);

export const isPulseBankAccountActionable = (account: PulseBankAccount) =>
  account.isActionRequired === true &&
  typeof account.dueBy === "number" &&
  account.dueBy >= 0;

export const pluralize = (count: number, singular: string, plural?: string) =>
  `${count} ${count === 1 ? singular : plural || `${singular}s`}`;

export const sortByPriority = <T>(
  entries: T[],
  getPriority: (entry: T) => number,
  getRecency?: (entry: T) => number
): T[] =>
  entries
    .map((entry, index) => ({
      entry,
      index,
      priority: getPriority(entry),
      recency: getRecency?.(entry) || 0,
    }))
    .sort(
      (left, right) =>
        left.priority - right.priority ||
        right.recency - left.recency ||
        left.index - right.index
    )
    .map(({ entry }) => entry);

export const isBankStatementPdf = (file: File) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

export const validateBankStatementFile = (file: File) => {
  if (!isBankStatementPdf(file))
    return "Only PDF bank statements can be uploaded.";
  if (file.size > MAX_FILE_SIZE)
    return `File size exceeds ${MAX_FILE_SIZE_LABEL}.`;
  return null;
};

export const getRequiredStatementPeriod = () => {
  const startDate = moment().subtract(12, "months").startOf("month");
  const endDate = moment();
  return {
    startDate,
    endDate,
    formattedText: `${startDate.format("MMM D, YYYY")} to ${endDate.format(
      "MMM D, YYYY"
    )}`,
  };
};

export const getDocumentName = (doc: PulseDocSummary) => {
  if (doc.fileName) return doc.fileName;
  if (!doc.s3FileKey) return `Document #${doc.id}`;
  const keyName = doc.s3FileKey.split("/").pop() || doc.s3FileKey;
  try {
    return decodeURIComponent(keyName);
  } catch (_error) {
    return keyName;
  }
};

export const maskAccountNumber = (accountNumber?: string) => {
  if (!accountNumber) return "";
  return `•• ${accountNumber.slice(-4)}`;
};

export const uniqueDocuments = (documents: PulseDocSummary[]) => {
  const byId = new Map<number, PulseDocSummary>();
  documents.forEach((doc) => {
    byId.set(doc.id, { ...byId.get(doc.id), ...doc });
  });
  return Array.from(byId.values());
};

export const pipelineStatusFromAccount = (
  account: PulseBankAccount
): PulsePipelineStatus => {
  if (account.pipelineStatus) return account.pipelineStatus;
  return pipelineStatusFromAnalysisStatus(
    account.lastAnalysis?.status,
    account.documents?.length ? "processing" : "idle"
  );
};

export const pipelineStatusFromAnalysisStatus = (
  status?: string | null,
  fallback: PulsePipelineStatus = "idle"
): PulsePipelineStatus => {
  const normalizedStatus = String(status || "").toUpperCase();
  if (
    normalizedStatus === "VERIFICATION_SUCCESS" ||
    normalizedStatus === "ERROR_OVERRIDED" ||
    normalizedStatus === "ERROR_OVERRIDDEN"
  ) {
    return "complete";
  }
  if (
    normalizedStatus === "FAILURE" ||
    normalizedStatus === "REQUEST_TIMED_OUT"
  ) {
    return "failed";
  }
  if (normalizedStatus === "VERIFYING" || normalizedStatus === "PROCESSING") {
    return "processing";
  }
  return fallback;
};

// A FAILURE counts as an error only while `errors` still lists a label, so
// clearing the labels clears the error. Timeouts always count.
export const isAccountFailed = (account: PulseBankAccount) => {
  if (pipelineStatusFromAccount(account) !== "failed") return false;
  const analysis = account.lastAnalysis;
  return (
    String(analysis?.status || "").toUpperCase() === "REQUEST_TIMED_OUT" ||
    !!analysis?.errorDetails?.errors?.length
  );
};

export const hasVisibleRangeError = (account: PulseBankAccount) => {
  const errorDetails = account.lastAnalysis?.errorDetails;
  const { transactionRanges, balanceRanges } =
    getVisibleRangeErrors(errorDetails);
  const consentMap = errorDetails?.noTransactionConsent || {};
  return (
    balanceRanges.length > 0 ||
    transactionRanges.some(
      (range) => !consentMap[getTransactionConsentKey(range)]?.consented
    )
  );
};

const DOCUMENT_COMPLETED_STATUSES = new Set([
  "COMPLETED",
  "COMPLETE",
  "SUCCESS",
]);

const DOCUMENT_FAILED_STATUSES = new Set([
  "FAILED",
  "IDENTIFICATION_FAILED",
]);

const DELETABLE_DOCUMENT_OCR_STATUSES = new Set([
  OCR_COMPLETED_STATUS,
  NOT_SUBMITTED_STATUS,
  "FAILED",
  "IDENTIFICATION_FAILED",
]);

const normalizeDocumentStatus = (status?: string | null) =>
  String(status || "").trim().toUpperCase();

export const isIdentificationFailure = (doc: PulseDocSummary) =>
  DOCUMENT_FAILED_STATUSES.has(
    normalizeDocumentStatus(doc.ocrProcessingStatus)
  );

export const isDocumentIdentificationFailed = (doc: PulseDocSummary) =>
  normalizeDocumentStatus(doc.ocrProcessingStatus) ===
  "IDENTIFICATION_FAILED";

export const isDocumentPasswordRequired = (doc: PulseDocSummary) =>
  normalizeDocumentStatus(doc.status) === REQUIRED_PASSWORD_STATUS;

export const isDocumentNotSubmitted = (doc: PulseDocSummary) =>
  normalizeDocumentStatus(doc.ocrProcessingStatus) === NOT_SUBMITTED_STATUS ||
  normalizeDocumentStatus(doc.status) === NOT_SUBMITTED_STATUS;

export const isDocumentSubmittable = (doc: PulseDocSummary) =>
  isDocumentNotSubmitted(doc) && !isDocumentPasswordRequired(doc);

export const isDocumentAwaitingSubmission = (doc: PulseDocSummary) =>
  isDocumentSubmittable(doc) ||
  normalizeDocumentStatus(doc.status) === SUBMITTING_STATUS;

export const isDocumentCompleted = (doc: PulseDocSummary) =>
  DOCUMENT_COMPLETED_STATUSES.has(
    normalizeDocumentStatus(doc.ocrProcessingStatus)
  ) ||
  DOCUMENT_COMPLETED_STATUSES.has(normalizeDocumentStatus(doc.status));

export const isDocumentFailed = (doc: PulseDocSummary) =>
  !!doc.errorMessage ||
  DOCUMENT_FAILED_STATUSES.has(
    normalizeDocumentStatus(doc.ocrProcessingStatus)
  ) ||
  DOCUMENT_FAILED_STATUSES.has(normalizeDocumentStatus(doc.status));

export const isDocumentDeletable = (doc: PulseDocSummary) => {
  const ocrStatus = normalizeDocumentStatus(doc.ocrProcessingStatus);
  if (ocrStatus) return DELETABLE_DOCUMENT_OCR_STATUSES.has(ocrStatus);

  return isDocumentNotSubmitted(doc) || isDocumentPasswordRequired(doc);
};

export const hasProcessingDocuments = (documents: PulseDocSummary[]) =>
  documents.some((doc) => !isDocumentDeletable(doc));

export const isSameRangeUpload = (
  left?: PulseRangeUploadInput | PulseUploadedForRange | null,
  right?: PulseRangeUploadInput | PulseUploadedForRange | null
) =>
  !!left &&
  !!right &&
  left.errorType === right.errorType &&
  left.rangeStart === right.rangeStart &&
  left.rangeEnd === right.rangeEnd;

// A document uploaded against a missing period keeps that row in its processing
// state until the next analysis result lands. Password-required counts as
// pending (the user is still working on it), and a document whose OCR has
// finished keeps counting while the account is still re-analysing.
export const isRangeUploadPending = (
  doc: PulseDocSummary,
  accountPipelineActive: boolean
) =>
  !isDocumentFailed(doc) &&
  (!isDocumentCompleted(doc) || accountPipelineActive);

export const truncateBankName = (bankName: string, maxCharacters = 30) => {
  const characters = Array.from(bankName);
  if (characters.length <= maxCharacters) return bankName;
  return `${characters.slice(0, maxCharacters).join("")}…`;
};

export const getAccountExpansionStorageKey = (
  companyId: number,
  accountKey: number | "new-account"
) => `bank-statements:expanded:${companyId || "company"}:${accountKey}`;

export const getStoredAccountExpansion = (storageKey: string) => {
  try {
    if (typeof window === "undefined") return false;
    const value = window.sessionStorage.getItem(storageKey);
    return value == null ? false : value === "true";
  } catch (_error) {
    return false;
  }
};

export const storeAccountExpansion = (
  storageKey: string,
  expanded: boolean
) => {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(storageKey, String(expanded));
  } catch (_error) {
  }
};

export const isUnresolvedDocumentProcessing = (
  doc: PulseDocSummary
) => {
  if (
    isDocumentAwaitingSubmission(doc) ||
    isDocumentPasswordRequired(doc) ||
    doc.errorMessage ||
    isIdentificationFailure(doc)
  ) {
    return false;
  }
  return !DOCUMENT_COMPLETED_STATUSES.has(
    normalizeDocumentStatus(doc.ocrProcessingStatus)
  );
};

export const getTransactionConsentKey = (range: string[]) =>
  `${range[0] || ""}_${range[1] || ""}`;

export const formatDateRange = (
  startDate?: string | null,
  endDate?: string | null
) => {
  if (!startDate || !endDate) return null;
  const start = moment(startDate, moment.ISO_8601, true);
  const end = moment(endDate, moment.ISO_8601, true);
  if (!start.isValid() || !end.isValid()) return null;
  return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

export const formatMissingRange = (range: string[]) => {
  const [startDate, endDate] = range;
  if (!startDate || !endDate) return "Missing period";
  return formatDateRange(startDate, endDate) || `${startDate} - ${endDate}`;
};
